import React, { useState, useCallback, useRef } from "react";
import styled from "styled-components";
import { Step, ChordProgression, ChordMapping } from "../types";

interface FXMappingViewProps {
  pattern: Step[];
  currentStep: number;
  progression: ChordProgression;
  chordMapping: ChordMapping;
  mappingMode: string;
  onStepFXMixChange: (stepIndex: number, fxType: string, value: number) => void;
  getCurrentGlobalValue: () => number;
  onMappingModeChange?: (mode: string | null) => void;
  onClearMapping?: (fxType: string) => void;
  stepsCount: number;
}

const FXMappingView: React.FC<FXMappingViewProps> = ({
  pattern,
  currentStep,
  mappingMode,
  onStepFXMixChange,
  getCurrentGlobalValue,
  onMappingModeChange,
  onClearMapping,
  stepsCount,
}) => {
  const [isPainting, setIsPainting] = useState<boolean>(false);
  const lastPaintedStepRef = useRef<number | null>(null);
  const mappingRootRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const barAdjustmentStepRef = useRef<number | null>(null);

  // Calculate number of rows (max 16 steps per row)
  const rowsCount = Math.ceil(stepsCount / 16);
  const stepsPerRow = 16;

  const getStepFXValue = useCallback(
    (stepIndex: number): number => {
      const step = pattern[stepIndex];
      if (!step) return getCurrentGlobalValue();

      const fxMixes = step.fxMixes;
      if (!fxMixes) return getCurrentGlobalValue();

      // For filter, the value is stored as Hz, need to convert to 0-1
      if (mappingMode === "filter") {
        const FILTER_MIN = 20;
        const FILTER_MAX = 10000;
        const FILTER_RANGE = FILTER_MAX - FILTER_MIN;
        const filterHz = fxMixes.filter ?? getCurrentGlobalValue();
        return Math.min(Math.max((filterHz - FILTER_MIN) / FILTER_RANGE, 0), 1);
      }

      // For other FX, value is already 0-1
      const value = fxMixes[mappingMode as keyof typeof fxMixes];
      return value !== undefined ? value : getCurrentGlobalValue();
    },
    [pattern, mappingMode, getCurrentGlobalValue]
  );

  // Helper function to update a step's value based on Y position within that step
  const updateStepValueFromY = useCallback(
    (stepIndex: number, clientY: number) => {
      const step = pattern[stepIndex];
      if (!step || !step.active) return;

      // Find the step element to get its bounding rect
      const stepElement = document.getElementById(
        `mapping-step-bar-wrapper-${stepIndex}`
      );
      if (!stepElement) return;

      const stepRect = stepElement.getBoundingClientRect();
      const relativeY = clientY - stepRect.top;
      const normalizedY = Math.max(
        0,
        Math.min(1, 1 - relativeY / stepRect.height)
      );

      // For filter, convert 0-1 to Hz
      if (mappingMode === "filter") {
        const FILTER_MIN = 20;
        const FILTER_MAX = 10000;
        const FILTER_RANGE = FILTER_MAX - FILTER_MIN;
        const filterHz = FILTER_MIN + normalizedY * FILTER_RANGE;
        onStepFXMixChange(stepIndex, mappingMode, filterHz);
      } else {
        onStepFXMixChange(stepIndex, mappingMode, normalizedY);
      }
    },
    [pattern, mappingMode, onStepFXMixChange]
  );

  // Helper function to calculate step index from position when elementFromPoint doesn't find a step
  const calculateStepIndexFromPosition = useCallback(
    (clientX: number, clientY: number): number | null => {
      if (!containerRef.current) return null;

      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();

      // Check if click is within container bounds
      if (
        clientX < containerRect.left ||
        clientX > containerRect.right ||
        clientY < containerRect.top ||
        clientY > containerRect.bottom
      ) {
        return null;
      }

      // Calculate relative position within container
      const relativeY = clientY - containerRect.top;

      // Find which row
      const rowHeight = containerRect.height / rowsCount;
      const rowIndex = Math.floor(relativeY / rowHeight);
      const clampedRowIndex = Math.max(0, Math.min(rowIndex, rowsCount - 1));

      // Find which step in the row by checking all step containers
      const allSteps = container.querySelectorAll(
        '[id^="mapping-step-"]:not([id*="bar"])'
      );
      if (allSteps.length === 0) return null;

      // Get steps in this row
      const rowStartIndex = clampedRowIndex * stepsPerRow;
      const rowEndIndex = Math.min(rowStartIndex + stepsPerRow, stepsCount);

      // Find the step whose X bounds contain the click
      for (let i = rowStartIndex; i < rowEndIndex; i++) {
        const stepElement = document.getElementById(`mapping-step-${i}`);
        if (!stepElement) continue;

        const stepRect = stepElement.getBoundingClientRect();
        if (clientX >= stepRect.left && clientX <= stepRect.right) {
          return i;
        }
      }

      // Fallback: estimate based on step width
      if (allSteps.length > 0) {
        const firstStep = allSteps[0] as HTMLElement;
        const stepRect = firstStep.getBoundingClientRect();
        const stepWidth = stepRect.width;
        const gap = 4; // gap between steps
        const relativeX = clientX - containerRect.left;
        const stepIndexInRow = Math.floor(relativeX / (stepWidth + gap));
        const clampedStepIndex = Math.max(
          0,
          Math.min(stepIndexInRow, stepsPerRow - 1)
        );
        const stepIndex = clampedRowIndex * stepsPerRow + clampedStepIndex;
        return stepIndex < stepsCount ? stepIndex : null;
      }

      return null;
    },
    [rowsCount, stepsPerRow, stepsCount]
  );

  // Helper function to find step index from DOM element
  const findStepIndexFromElement = useCallback(
    (element: Element | null): number | null => {
      if (!element) return null;

      // Traverse up the DOM tree to find the step container or wrapper
      let current: Element | null = element;
      while (current) {
        const id = current.getAttribute("id");
        if (id) {
          // Check for step container (mapping-step-{index})
          if (
            id.startsWith("mapping-step-") &&
            !id.startsWith("mapping-step-bar")
          ) {
            const stepIndexMatch = id.match(/mapping-step-(\d+)/);
            if (stepIndexMatch) {
              return parseInt(stepIndexMatch[1], 10);
            }
          }
          // Check for step bar wrapper (mapping-step-bar-wrapper-{index})
          if (id.startsWith("mapping-step-bar-wrapper-")) {
            const stepIndexMatch = id.match(/mapping-step-bar-wrapper-(\d+)/);
            if (stepIndexMatch) {
              return parseInt(stepIndexMatch[1], 10);
            }
          }
        }
        current = current.parentElement;
      }
      return null;
    },
    []
  );

  const handleContainerMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // Don't start painting if clicking on a step bar (let that handler take precedence)
      const target = e.target as HTMLElement;
      if (target.closest('[id^="mapping-step-bar-wrapper-"]')) {
        return;
      }

      e.preventDefault();

      // First try to find step from element under cursor
      const elementUnderCursor = document.elementFromPoint(
        e.clientX,
        e.clientY
      );
      let stepIndex: number | null = null;

      if (elementUnderCursor) {
        stepIndex = findStepIndexFromElement(elementUnderCursor);
      }

      // If we didn't find a step element, calculate from position
      if (stepIndex === null) {
        stepIndex = calculateStepIndexFromPosition(e.clientX, e.clientY);
      }

      if (stepIndex === null || stepIndex < 0 || stepIndex >= pattern.length)
        return;

      setIsPainting(true);
      lastPaintedStepRef.current = stepIndex;
      // Not adjusting a specific bar - this is cross-step painting
      barAdjustmentStepRef.current = null;

      // Only update if step is active
      const step = pattern[stepIndex];
      if (step && step.active) {
        updateStepValueFromY(stepIndex, e.clientY);
      }
    },
    [
      pattern,
      findStepIndexFromElement,
      calculateStepIndexFromPosition,
      updateStepValueFromY,
    ]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, stepIndex: number) => {
      const step = pattern[stepIndex];
      if (!step || !step.active) return;

      e.preventDefault();
      setIsPainting(true);
      lastPaintedStepRef.current = stepIndex;
      // Track that we're adjusting a specific bar
      barAdjustmentStepRef.current = stepIndex;

      // Immediately update the clicked step's value
      updateStepValueFromY(stepIndex, e.clientY);
    },
    [pattern, updateStepValueFromY]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isPainting || !containerRef.current) return;

      // Check if we're still within the container
      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      if (
        e.clientX < containerRect.left ||
        e.clientX > containerRect.right ||
        e.clientY < containerRect.top ||
        e.clientY > containerRect.bottom
      ) {
        return;
      }

      // Find the element under the cursor
      const elementUnderCursor = document.elementFromPoint(
        e.clientX,
        e.clientY
      );

      // Try to find step index from element first
      let stepIndex: number | null = null;
      if (elementUnderCursor) {
        stepIndex = findStepIndexFromElement(elementUnderCursor);
      }

      // Fallback to position-based calculation if element-based lookup failed
      if (stepIndex === null) {
        stepIndex = calculateStepIndexFromPosition(e.clientX, e.clientY);
      }

      if (stepIndex === null || stepIndex < 0 || stepIndex >= pattern.length)
        return;

      // If we're adjusting a specific bar, check if we've moved to a different bar
      if (barAdjustmentStepRef.current !== null) {
        const currentBarStep = barAdjustmentStepRef.current;

        // If we're over a different bar, switch to controlling that bar
        if (stepIndex !== currentBarStep) {
          const newStep = pattern[stepIndex];
          if (newStep && newStep.active) {
            barAdjustmentStepRef.current = stepIndex;
            updateStepValueFromY(stepIndex, e.clientY);
            lastPaintedStepRef.current = stepIndex;
          }
          return;
        }

        // Otherwise, continue adjusting the current bar
        const step = pattern[currentBarStep];
        if (step && step.active) {
          updateStepValueFromY(currentBarStep, e.clientY);
        }
        return;
      }

      // Otherwise, this is cross-step painting mode
      // Skip if this is the same step we just painted (prevent redundant updates)
      if (stepIndex === lastPaintedStepRef.current) return;

      // Check if step is active before updating
      const step = pattern[stepIndex];
      if (!step || !step.active) return;

      // Update the step value
      updateStepValueFromY(stepIndex, e.clientY);
      lastPaintedStepRef.current = stepIndex;
    },
    [
      isPainting,
      pattern,
      findStepIndexFromElement,
      calculateStepIndexFromPosition,
      updateStepValueFromY,
    ]
  );

  const handleMouseUp = useCallback(() => {
    setIsPainting(false);
    lastPaintedStepRef.current = null;
    barAdjustmentStepRef.current = null;
  }, []);

  React.useEffect(() => {
    if (isPainting) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isPainting, handleMouseMove, handleMouseUp]);

  React.useEffect(() => {
    const handleOutsideMouseDown = (event: MouseEvent) => {
      if (!onMappingModeChange) return;
      const target = event.target as HTMLElement | null;
      if (!target || !mappingRootRef.current) return;

      // Allow the FX mapping toggle button to handle its own onClick toggle logic.
      if (target.closest('[data-mapping-toggle="true"]')) {
        return;
      }

      if (!mappingRootRef.current.contains(target)) {
        onMappingModeChange(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideMouseDown);
    return () => {
      document.removeEventListener("mousedown", handleOutsideMouseDown);
    };
  }, [onMappingModeChange]);

  const handleClear = useCallback(() => {
    if (onClearMapping) {
      onClearMapping(mappingMode);
    }
    if (onMappingModeChange) {
      onMappingModeChange(null);
    }
  }, [mappingMode, onClearMapping, onMappingModeChange]);

  // Format FX name for display (capitalize first letter)
  const fxName = mappingMode.charAt(0).toUpperCase() + mappingMode.slice(1);

  // Group steps into rows
  const rows: Step[][] = [];
  for (let rowIndex = 0; rowIndex < rowsCount; rowIndex++) {
    const startIndex = rowIndex * stepsPerRow;
    const endIndex = Math.min(startIndex + stepsPerRow, stepsCount);
    rows.push(pattern.slice(startIndex, endIndex));
  }

  return (
    <MappingViewRoot id="mapping-view-root" ref={mappingRootRef}>
      <MappingBar id="mapping-bar">
        <MappingTitle id="mapping-title">Mapping {fxName}</MappingTitle>
        <ClearButton id="clear-mapping-button" onClick={handleClear}>
          Clear
        </ClearButton>
      </MappingBar>
      <MappingContainer
        id="fx-mapping-view"
        ref={containerRef}
        $rowsCount={rowsCount}
        onMouseDown={handleContainerMouseDown}
      >
        {rows.map((rowSteps, rowIndex) => {
          const rowStartIndex = rowIndex * stepsPerRow;
          return (
            <MappingRow key={rowIndex} $rowsCount={rowsCount}>
              {rowSteps.map((step, stepIndexInRow) => {
                const index = rowStartIndex + stepIndexInRow;
                const fxValue = getStepFXValue(index);
                const isActive = step.active;
                const isCurrent = currentStep === index;

                // Check if this step has mapping applied
                const hasMapping = Boolean(
                  step.fxMixes &&
                    step.fxMixes[mappingMode as keyof typeof step.fxMixes] !==
                      undefined
                );

                // Use #F58413 for steps with mapping, #714C27 for active steps without mapping (empty state), transparent for inactive
                const barColor = hasMapping
                  ? "#F58413"
                  : isActive
                  ? "#714C27"
                  : "transparent";

                return (
                  <StepContainer
                    key={index}
                    id={`mapping-step-${index}`}
                    $isActive={isActive}
                    $isCurrent={isCurrent}
                    $isDragging={false}
                  >
                    <StepBarWrapper
                      id={`mapping-step-bar-wrapper-${index}`}
                      onMouseDown={(e) => handleMouseDown(e, index)}
                      $isActive={isActive}
                      $isDragging={false}
                      $hasMapping={hasMapping}
                    >
                      <StepBar
                        id={`mapping-step-bar-${index}`}
                        $height={fxValue}
                        $backgroundColor={barColor}
                        $isActive={isActive}
                        $isCurrent={isCurrent}
                      />
                    </StepBarWrapper>
                  </StepContainer>
                );
              })}
            </MappingRow>
          );
        })}
      </MappingContainer>
    </MappingViewRoot>
  );
};

export default FXMappingView;

// Styled Components
const MappingBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 20px 4px 20px;
  margin-bottom: 0;
  width: 100%;
  box-sizing: border-box;
  position: relative;
`;

const MappingViewRoot = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const MappingTitle = styled.div`
  font-family: "Dogica Pixel", monospace;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: 1px;
`;

const MappingContainer = styled.div<{ $rowsCount: number }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: stretch;
  margin: 0 auto;
  width: 100%;
  padding: 16px;
  box-sizing: border-box;
  position: relative;
  flex: 1;
  min-height: 0;
`;

const MappingRow = styled.div<{ $rowsCount: number }>`
  display: flex;
  flex-direction: row;
  gap: 4px;
  justify-content: stretch;
  flex: 1 1 0;
  min-height: 0;
  height: calc(100% / ${(props) => props.$rowsCount});
`;

const ClearButton = styled.button`
  position: absolute;
  right: 20px;
  padding: 8px 16px;
  background: none;
  border: none;
  color: #f0bf5f;
  cursor: pointer;
  font-family: "Dogica Pixel", monospace;
  font-size: 10px;
  letter-spacing: 1px;
  transition: all 0.15s ease;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    transform: scale(0.96);
  }
`;

const StepContainer = styled.div<{
  $isActive: boolean;
  $isCurrent: boolean;
  $isDragging: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 2px;
  border-radius: 2px;
  border: 2px solid transparent;
  box-sizing: border-box;
  flex: 1 1 0;
  min-width: 0;
  position: relative;
  height: 100%;

  ${(props) =>
    props.$isCurrent &&
    `
    border: 2px solid rgba(255, 255, 255, 0.3);
  `}

  ${(props) =>
    props.$isCurrent &&
    props.$isActive &&
    `
    border: 2px solid rgba(255, 255, 255, 0.5);
  `}

  ${(props) =>
    props.$isDragging &&
    `
    border: 2px solid rgba(33, 150, 243, 0.8);
  `}
`;

const StepBarWrapper = styled.div<{
  $isActive: boolean;
  $isDragging: boolean;
  $hasMapping: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  cursor: ${(props) => (props.$isActive ? "ns-resize" : "default")};
  user-select: none;
  width: 100%;
  height: 100%;
  position: relative;
  background-color: ${(props) => (props.$isActive ? "#714C27" : "transparent")};
  border-radius: 3px;
`;

const StepBar = styled.div<{
  $height: number;
  $backgroundColor: string;
  $isActive: boolean;
  $isCurrent: boolean;
}>`
  width: 100%;
  height: ${(props) => props.$height * 100}%;
  min-height: ${(props) => (props.$height > 0 ? "4px" : "0")};
  border-radius: 3px;
  transition: ${(props) => (props.$isCurrent ? "none" : "all 0.1s ease")};
  background-color: ${(props) => props.$backgroundColor};
  box-shadow: ${(props) =>
    props.$isActive && props.$height > 0
      ? "0 0 4px rgba(255, 255, 255, 0.2)"
      : "none"};

  &:hover {
    opacity: ${(props) => (props.$isActive ? "0.9" : "1")};
  }
`;
